import {
  Column,
  ColumnDef,
  flexRender,
  Table as TanstackTable,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import _range from 'lodash/range';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSProperties } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { AggregateDataCheckbox } from './aggregate-data-checkbox';
import { Skeleton } from './skeleton';
import { Typography } from './typography';

interface DataTableProps<TData, TValue> {
  table: TanstackTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  showAggregateData?: boolean;
  setShowAggregateData?: (showAggregateData: boolean) => void;
  /**
   * Honor `columnDef.size` on unpinned columns. Dashboard tables only apply
   * size when a column is left-pinned; select-openings needs fixed widths
   * without pinning.
   */
  applyColumnSizes?: boolean;

  /** Overrides the default "N Openings" count label, e.g. "N campaigns". */
  countLabel?: React.ReactNode;
}

function getColumnLayoutStyle<TData>({
  column,
  applyColumnSizes,
  pinnedLeft,
  start,
}: {
  column: Column<TData, unknown>;
  applyColumnSizes: boolean;
  pinnedLeft: boolean;
  start: number;
}): CSSProperties | undefined {
  const honorSize =
    pinnedLeft || (applyColumnSizes && column.columnDef.size != null);
  if (!honorSize) return undefined;

  return {
    ...(pinnedLeft ? { left: start } : {}),
    width: column.getSize(),
    minWidth: column.getSize(),
    maxWidth: column.getSize(),
  };
}

export function DataTable<TData, TValue>({
  table,
  columns,
  isLoading = false,
  emptyState,
  showAggregateData = false,
  setShowAggregateData,
  applyColumnSizes = false,
  countLabel,
}: DataTableProps<TData, TValue>) {
  const intl = useIntl();

  const lastLeftLeafId = table.getLeftLeafColumns().at(-1)?.id;
  const hasPinnedColumns = !!lastLeftLeafId;
  const hasMultipleHeaderGroups = table.getHeaderGroups().length > 1;

  return (
    <div className="flex flex-col items-start self-stretch">
      <div className="border-fx-neutral-200 flex items-center justify-between self-stretch border-b bg-white p-4">
        <Typography variant="subtitle1">
          {`${table.getRowCount()} `}
          {countLabel ?? (
            <FormattedMessage
              defaultMessage="Openings"
              description="Count label: number of job openings in the table"
            />
          )}
        </Typography>
        {setShowAggregateData && (
          <AggregateDataCheckbox
            checked={showAggregateData}
            onCheckedChange={setShowAggregateData}
            label={intl.formatMessage({
              defaultMessage: 'Show aggregate data',
              description:
                'Toggle: include an extra row summing the whole table',
            })}
          />
        )}
      </div>
      <Table className={applyColumnSizes ? 'table-fixed' : undefined}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup, groupIndex) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isGroupRow = hasMultipleHeaderGroups && groupIndex === 0;
                // Group row: meta comes from the column itself (the group parent)
                // Leaf row: meta comes from the column's parent (the group it belongs to)
                const groupMeta = isGroupRow
                  ? header.column.columnDef.meta
                  : header.column.parent?.columnDef.meta;
                const hasGroupLabel =
                  isGroupRow && !header.isPlaceholder && groupMeta?.groupBg;

                // Narrow columns (select/menu/title/status) keep compact 8px padding.
                // All metric columns after status get 16px horizontal padding + overflow clip.
                const isNarrow = !!header.column.columnDef.meta?.narrowCell;

                const isPinned = header.column.getIsPinned();
                const wraps = !!header.column.columnDef.meta?.wrapCell;
                // Last sticky header: the header whose rightmost leaf is the last left-pinned leaf
                const headerRightmostLeafId =
                  header.column.columns.length > 0
                    ? header.column.columns.at(-1)?.id
                    : header.column.id;
                const isLastLeftPinned =
                  isPinned === 'left' &&
                  headerRightmostLeafId === lastLeftLeafId;

                // Sticky headers need an opaque background; group headers keep their
                // coloured band, everything else falls back to the thead bg.
                const headerBg =
                  isPinned === 'left'
                    ? (isGroupRow && groupMeta?.groupBg) || 'bg-fx-neutral-50'
                    : isGroupRow
                      ? groupMeta?.groupBg
                      : groupMeta?.cellBg;

                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={getColumnLayoutStyle({
                      column: header.column,
                      applyColumnSizes,
                      pinnedLeft: isPinned === 'left',
                      start: header.getStart(),
                    })}
                    className={clsx(
                      headerBg,
                      isPinned === 'left' && 'sticky z-20',
                      hasPinnedColumns && !isNarrow && 'px-4',
                      hasPinnedColumns &&
                        !isNarrow &&
                        !isLastLeftPinned &&
                        'overflow-hidden',
                      wraps && 'whitespace-normal',
                      isLastLeftPinned &&
                        'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                    )}
                  >
                    {hasGroupLabel ? (
                      <div className="flex items-center justify-center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    ) : isGroupRow ? null : (
                      <Typography variant="subtitle1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Typography>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            _range(table.getState().pagination.pageSize).map((_, rowIndex) => {
              return (
                <TableRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`skeleton-${rowIndex}`}
                >
                  {table.getAllLeafColumns().map(col => {
                    const isPinned = col.getIsPinned();
                    const cellBg = col.parent?.columnDef.meta?.cellBg;
                    const isLastLeftPinned =
                      isPinned === 'left' && col.id === lastLeftLeafId;
                    const isNarrowCol = !!col.columnDef.meta?.narrowCell;
                    const wraps = !!col.columnDef.meta?.wrapCell;
                    return (
                      <TableCell
                        key={col.id}
                        style={getColumnLayoutStyle({
                          column: col,
                          applyColumnSizes,
                          pinnedLeft: isPinned === 'left',
                          start: col.getStart('left'),
                        })}
                        className={clsx(
                          isPinned === 'left' ? 'sticky z-10 bg-white' : cellBg,
                          hasPinnedColumns && !isNarrowCol && 'px-4',
                          hasPinnedColumns &&
                            !isNarrowCol &&
                            !isLastLeftPinned &&
                            'overflow-hidden',
                          wraps && 'whitespace-normal',
                          isLastLeftPinned &&
                            'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                        )}
                      >
                        <Skeleton className="h-3 w-full" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map(cell => {
                  const isPinned = cell.column.getIsPinned();
                  const cellBg = cell.column.parent?.columnDef.meta?.cellBg;
                  const isLastLeftPinned =
                    isPinned === 'left' && cell.column.id === lastLeftLeafId;
                  const isNarrowCol = !!cell.column.columnDef.meta?.narrowCell;
                  const wraps = !!cell.column.columnDef.meta?.wrapCell;
                  return (
                    <TableCell
                      key={cell.id}
                      style={getColumnLayoutStyle({
                        column: cell.column,
                        applyColumnSizes,
                        pinnedLeft: isPinned === 'left',
                        start: cell.column.getStart('left'),
                      })}
                      className={clsx(
                        isPinned === 'left'
                          ? 'group-hover/row:bg-fx-neutral-50 group-data-[state=selected]/row:bg-fx-brand-50 sticky z-10 bg-white'
                          : cellBg,
                        hasPinnedColumns && !isNarrowCol && 'px-4',
                        hasPinnedColumns &&
                          !isNarrowCol &&
                          !isLastLeftPinned &&
                          'overflow-hidden',
                        wraps && 'whitespace-normal',
                        isLastLeftPinned &&
                          'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="min-h-24 py-16 text-center"
              >
                {emptyState}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
